// Speed Mahjong: riichi mahjong without turns. Everyone draws from one wall
// and discards at their own pace; calls are reserved ahead of time and wins are automatic.
// Shared by the browser (CPU matches) and the server (online rooms).
import Majiang from '@kobalab/majiang-core';

export const TILES = [...'mps']
  .flatMap((s) => Array.from({ length: 9 }, (_, i) => s + (i + 1)))
  .concat(Array.from({ length: 7 }, (_, i) => 'z' + (i + 1)));
export const WINDS = ['E', 'S', 'W', 'N'];
export const WIND_NAMES = ['East', 'South', 'West', 'North'];
export const CPU_NAMES = ['You', 'CPU 01', 'CPU 02', 'CPU 03'];
export const LOCAL_SPEED = { slow: 2200, normal: 1350, fast: 750 };
export const ONLINE_SPEED = { slow: 4000, normal: 3000, fast: 2000 };
export const ACTION_LIMIT = 5000;
export const RIICHI_STEP = 500;

const SUIT_NAMES = { m: 'Man', p: 'Pin', s: 'Sou' };
const HONOR_NAMES = ['', 'East', 'South', 'West', 'North', 'White', 'Green', 'Red'];

export function tileName(t) {
  return t[0] === 'z' ? HONOR_NAMES[+t[1]] : `${t[1]} ${SUIT_NAMES[t[0]]}`;
}

// Short corner label for players who don't read the characters on the tiles.
const HONOR_LABELS = ['', 'E', 'S', 'W', 'N', 'Wh', 'G', 'R'];
export function tileLabel(t) {
  return t[0] === 'z' ? HONOR_LABELS[+t[1]] : t[1] + t[0];
}

export const roundName = (round) => `East ${round + 1}`;

// Tiles in hand, sorted, with the freshly drawn tile last.
export function handTiles(hand, drawnLast = true) {
  const out = [];
  for (const t of TILES) for (let n = 0; n < hand._bingpai[t[0]][+t[1]]; n++) out.push(t);
  if (drawnLast && hand._zimo?.length === 2) {
    out.splice(out.indexOf(hand._zimo), 1);
    out.push(hand._zimo);
  }
  return out;
}

export function meldTiles(meld) {
  return (meld.match(/\d/g) || []).map((d) => meld[0] + d);
}

export function direction(from, to) {
  return ['', '+', '=', '-'][(from - to + 4) % 4];
}

export function reservationKey(type, tile, meld) {
  return `${type}:${tile}:${meld.replace(/[+=-]/g, '')}`;
}

// Every pon/chi this hand could make if the tile were discarded.
export function callOptions(hand) {
  if (hand.lizhi) return [];
  const h = hand.clone();
  h._zimo = null;
  return TILES.flatMap((t) => [
    ...(h.get_peng_mianzi(t + '-') || []).map((meld) => ({ type: 'pon', tile: t, meld })),
    ...(h.get_chi_mianzi(t + '-', false) || []).map((meld) => ({ type: 'chi', tile: t, meld })),
  ]).map((o) => ({ ...o, key: reservationKey(o.type, o.tile, o.meld) }));
}

const YAKU = {
  立直: 'Riichi', ダブル立直: 'Double Riichi', 一発: 'Ippatsu', 海底摸月: 'Haitei', 河底撈魚: 'Houtei',
  嶺上開花: 'Rinshan', 槍槓: 'Chankan', 天和: 'Tenhou', 地和: 'Chiihou', 門前清自摸和: 'Menzen Tsumo',
  '場風 東': 'Round Wind East', '場風 南': 'Round Wind South', '場風 西': 'Round Wind West', '場風 北': 'Round Wind North',
  '自風 東': 'Seat Wind East', '自風 南': 'Seat Wind South', '自風 西': 'Seat Wind West', '自風 北': 'Seat Wind North',
  '翻牌 白': 'White Dragon', '翻牌 發': 'Green Dragon', '翻牌 中': 'Red Dragon',
  平和: 'Pinfu', 断幺九: 'Tanyao', 一盃口: 'Iipeikou', 三色同順: 'Sanshoku', 一気通貫: 'Ittsu', 混全帯幺九: 'Chanta',
  七対子: 'Chiitoitsu', 対々和: 'Toitoi', 三暗刻: 'San Ankou', 三槓子: 'San Kantsu', 三色同刻: 'Sanshoku Doukou',
  混老頭: 'Honroutou', 小三元: 'Shousangen', 混一色: 'Honitsu', 純全帯幺九: 'Junchan', 二盃口: 'Ryanpeikou', 清一色: 'Chinitsu',
  国士無双十三面: 'Kokushi (13-wait)', 国士無双: 'Kokushi Musou', 四暗刻単騎: 'Suuankou Tanki', 四暗刻: 'Suuankou',
  大三元: 'Daisangen', 大四喜: 'Daisuushii', 小四喜: 'Shousuushii', 字一色: 'Tsuuiisou', 緑一色: 'Ryuuiisou',
  清老頭: 'Chinroutou', 四槓子: 'Suukantsu', 純正九蓮宝燈: 'Pure Nine Gates', 九蓮宝燈: 'Nine Gates',
  ドラ: 'Dora', 赤ドラ: 'Red Dora', 裏ドラ: 'Ura Dora',
};
export const yakuName = (name) => YAKU[name] || name;

function secureRandom() {
  if (globalThis.crypto?.getRandomValues) {
    const a = new Uint32Array(1);
    crypto.getRandomValues(a);
    return a[0] / 4294967296;
  }
  return Math.random();
}

export class SpeedGame {
  constructor({ rng = secureRandom, onChange = () => {}, humans = [0], names = CPU_NAMES, cpuTiming = 'local' } = {}) {
    this.rng = rng;
    this.onChange = onChange;
    this.humans = [...humans];
    this.names = [...names];
    this.cpuTiming = cpuTiming;
    this.speed = 'normal';
    this.newMatch();
  }

  notify(event = 'update', message = '') {
    this.revision++;
    this.lastEvent = { event, message, id: this.revision };
    if (message) {
      this.log.unshift({ message, time: this.elapsed });
      this.log = this.log.slice(0, 40);
    }
    this.onChange(this.lastEvent);
  }

  newMatch() {
    this.round = 0;
    this.scores = [25000, 25000, 25000, 25000];
    this.pot = 0;
    this.revision = 0;
    this.prepare();
  }

  prepare() {
    const tiles = TILES.flatMap((t) => [t, t, t, t]);
    for (let i = tiles.length - 1; i > 0; i--) {
      const j = Math.floor(this.rng() * (i + 1));
      [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
    }
    this.dead = tiles.splice(0, 14);
    this.wall = tiles;
    this.dealer = this.round % 4;
    this.elapsed = 0;
    this.log = [];
    const before = this.players;
    this.players = this.names.map((name, id) => ({
      id,
      name,
      hand: new Majiang.Shoupai(this.wall.splice(0, 13)),
      river: [],
      next: 300 + this.rng() * 700,
      deadline: ACTION_LIMIT,
      draws: 0,
      phase: 0,
      timeoutCount: 0,
      lastTimeout: null,
      reservations: [],
      autoDraw: before?.[id]?.autoDraw ?? false, // the Auto draw setting carries over between hands
      holdUntil: 0,
    }));
    if (this.cpuTiming === 'online') this.rescheduleCPUs();
    this.status = 'ready';
    this.riichiArmed = false;
    this.result = null;
    this.lastDiscard = null;
    this.lastTimeout = null;
    this.notify('ready');
  }

  get reservations() {
    return this.players[0].reservations;
  }
  set reservations(v) {
    this.players[0].reservations = v;
  }
  get autoDraw() {
    return this.players[0].autoDraw;
  }

  isHuman(id) {
    return this.humans.includes(id);
  }

  start() {
    if (this.status !== 'ready') return false;
    this.status = 'playing';
    for (const p of this.players) p.deadline = this.elapsed + ACTION_LIMIT;
    this.notify('start', 'Round started. You have 5 seconds each to draw and to discard.');
    return true;
  }

  pause() {
    if (this.status !== 'playing') return false;
    this.status = 'paused';
    this.notify('pause');
    return true;
  }

  resume() {
    if (this.status !== 'paused') return false;
    this.status = 'playing';
    this.notify('resume');
    return true;
  }

  nextRound() {
    if (this.status !== 'ended' || this.round >= 3) return false;
    this.round++;
    this.prepare();
    this.start();
    return true;
  }

  seat(id) {
    return (id - this.dealer + 4) % 4;
  }

  setSpeed(speed) {
    if (!Object.hasOwn(LOCAL_SPEED, speed)) throw new Error('Unknown speed.');
    const changed = this.speed !== speed;
    this.speed = speed;
    if (changed && this.cpuTiming === 'online') this.rescheduleCPUs();
    this.notify('settings');
  }

  cpuDelay(kind) {
    if (this.cpuTiming === 'online') {
      const cycle = ONLINE_SPEED[this.speed];
      return kind === 'discard' ? cycle * 0.3 : cycle * 0.7 * (0.85 + this.rng() * 0.15);
    }
    return kind === 'discard'
      ? Math.max(180, LOCAL_SPEED[this.speed] * 0.26)
      : LOCAL_SPEED[this.speed] * (0.72 + this.rng() * 0.28);
  }

  rescheduleCPUs() {
    for (const p of this.players) {
      if (!this.isHuman(p.id) && !p.hand.lizhi) p.next = p.deadline - ACTION_LIMIT + this.cpuDelay(p.hand._zimo ? 'discard' : 'draw');
    }
  }

  waits(id) {
    const hand = this.players[id].hand;
    return hand._zimo || Majiang.Util.xiangting(hand) !== 0 ? [] : Majiang.Util.tingpai(hand) || [];
  }

  furiten(id) {
    const waits = this.waits(id);
    return this.players[id].river.some((r) => waits.includes(r.tile));
  }

  // Score a win for player `id`: tsumo when from === null, otherwise ron on `tile` discarded by `from`.
  score(id, from = null, tile = null) {
    const p = this.players[id];
    if ((from !== null && (p.hand._zimo || this.furiten(id))) || (from === null && p.hand._zimo?.length !== 2)) return null;
    const result = Majiang.Util.hule(
      p.hand,
      from === null ? null : tile + direction(from, id),
      Majiang.Util.hule_param({
        rule: Majiang.rule({ 赤牌: { m: 0, p: 0, s: 0 }, 役満パオあり: true }),
        zhuangfeng: 0,
        menfeng: this.seat(id),
        lizhi: p.hand.lizhi ? 1 : 0,
        baopai: [this.dead[0]],
        fubaopai: p.hand.lizhi ? [this.dead[1]] : null,
        lizhibang: this.pot,
        yifa: false,
        haidi: 0,
        tianhu: 0,
      }),
    );
    return result?.hupai?.length ? result : null;
  }

  riichiDiscards(id = 0) {
    const p = this.players[id];
    if (!p.hand._zimo || !p.hand.menqian || p.hand.lizhi || this.scores[id] < 1000 || this.wall.length < 4) return [];
    return (p.hand.get_dapai() || []).filter((t) => Majiang.Util.xiangting(p.hand.clone().dapai(t)) === 0);
  }

  armRiichi() {
    if (this.status !== 'playing') return false;
    if (this.riichiArmed) {
      this.riichiArmed = false;
      this.notify();
      return true;
    }
    if (!this.riichiDiscards().length) return false;
    this.riichiArmed = true;
    this.notify();
    return true;
  }

  setAutoDraw(value, id = 0) {
    const p = this.players[id];
    p.autoDraw = !!value;
    if (value && !p.hand.lizhi) p.next = this.elapsed + 900;
    this.notify();
  }

  toggleReservation(key) {
    if (!['ready', 'playing', 'paused'].includes(this.status)) return false;
    const i = this.reservations.findIndex((r) => r.key === key);
    if (i >= 0) {
      this.reservations.splice(i, 1);
      this.notify('reservation');
      return true;
    }
    const option = callOptions(this.players[0].hand).find((o) => o.key === key);
    if (!option) return false;
    this.reservations.push(option);
    this.notify('reservation');
    return true;
  }

  clearReservations() {
    this.reservations = [];
    this.notify('reservation');
  }

  setReservations(id, keys) {
    const options = callOptions(this.players[id].hand);
    const chosen = keys.map((k) => options.find((o) => o.key === k));
    if (chosen.some((o) => !o) || new Set(keys).size !== keys.length) return false;
    this.players[id].reservations = chosen;
    this.notify('reservation');
    return true;
  }

  pruneReservations() {
    for (const p of this.players) {
      if (!p.reservations.length) continue;
      const keys = new Set(callOptions(p.hand).map((o) => o.key));
      p.reservations = p.reservations.filter((r) => keys.has(r.key));
    }
  }

  draw(id = 0, { automatic = false } = {}) {
    const p = this.players[id];
    if (this.status !== 'playing' || p.hand._zimo || !this.wall.length || (p.hand.lizhi && !automatic)) return false;
    const tile = this.wall.pop();
    p.hand.zimo(tile);
    p.draws++;
    p.phase++;
    p.deadline = this.elapsed + ACTION_LIMIT;
    p.next = this.elapsed + (p.hand.lizhi ? RIICHI_STEP : this.isHuman(id) ? 900 : this.cpuDelay('discard'));
    const win = this.score(id);
    if (win) this.win(id, null, tile, win);
    else this.notify('draw');
    return true;
  }

  discard(id, tile, riichi = false, { automatic = false } = {}) {
    const p = this.players[id];
    if (this.status !== 'playing' || !p.hand._zimo || (p.hand.lizhi && !automatic)) return false;
    const legal = p.hand.get_dapai() || [];
    const choice = legal.includes(tile) ? tile : legal.find((t) => t.slice(0, 2) === tile.slice(0, 2));
    riichi = riichi || (id === 0 && this.riichiArmed);
    if (!choice || (riichi && !this.riichiDiscards(id).includes(choice))) return false;
    p.hand.dapai(choice + (riichi ? '*' : ''));
    p.phase++;
    if (id === 0) this.riichiArmed = false;
    tile = choice.slice(0, 2);
    const entry = { tile, called: false, riichi, id: this.revision + 1 };
    p.river.push(entry);
    this.lastDiscard = { player: id, tile };
    p.deadline = this.elapsed + ACTION_LIMIT;
    p.next = this.elapsed + (p.hand.lizhi ? RIICHI_STEP : this.isHuman(id) ? 900 : this.cpuDelay('draw'));

    // Ron first, in seat order after the discarder (head bump).
    for (let d = 1; d <= 3; d++) {
      const other = (id + d) % 4;
      const win = this.score(other, id, tile);
      if (win) {
        if (riichi) {
          p.hand._lizhi = false;
          entry.riichi = false;
        }
        this.win(other, id, tile, win);
        return true;
      }
    }
    if (riichi) {
      this.scores[id] -= 1000;
      this.pot++;
      this.log.unshift({ message: `${p.name} declared riichi`, time: this.elapsed });
    }

    // Then pon, then chi, each in seat order after the discarder.
    let call = null;
    for (const type of ['pon', 'chi']) {
      for (let d = 1; d <= 3; d++) {
        const other = (id + d) % 4;
        const hand = this.players[other].hand;
        if (hand._zimo || hand.lizhi) continue;
        const melds = type === 'pon' ? hand.get_peng_mianzi(tile + direction(id, other)) : hand.get_chi_mianzi(tile + direction(id, other));
        if (!melds?.length) continue;
        let meld = null;
        if (this.isHuman(other)) {
          const r = this.players[other].reservations.find((r) => r.type === type && r.tile === tile && melds.some((m) => reservationKey(type, tile, m) === r.key));
          if (r) meld = melds.find((m) => reservationKey(type, tile, m) === r.key);
        } else meld = this.cpuCall(other, tile, melds);
        if (meld) {
          call = { id: other, type, meld };
          break;
        }
      }
      if (call) break;
    }
    if (call) {
      const caller = this.players[call.id];
      caller.hand.fulou(call.meld);
      caller.phase++;
      entry.called = true;
      caller.next = this.elapsed + (this.cpuTiming === 'online' && !this.isHuman(call.id) ? this.cpuDelay('discard') : 350);
      caller.deadline = this.elapsed + ACTION_LIMIT;
      this.pruneReservations();
      this.notify('call', `${caller.name} called ${call.type === 'pon' ? 'pon' : 'chi'} on ${tileName(tile)}`);
    } else {
      this.pruneReservations();
      this.notify(riichi ? 'riichi' : 'discard', riichi ? `${p.name} declared riichi` : '');
    }
    this.checkExhausted();
    return true;
  }

  visibleCounts(id) {
    const counts = Object.fromEntries(TILES.map((t) => [t, 0]));
    for (const t of handTiles(this.players[id].hand)) counts[t]++;
    for (const p of this.players) {
      for (const r of p.river) if (!r.called) counts[r.tile]++;
      for (const m of p.hand._fulou) for (const t of meldTiles(m)) counts[t]++;
    }
    counts[this.dead[0]]++;
    return counts;
  }

  chooseDiscard(id, hand = this.players[id].hand) {
    const options = hand.get_dapai() || [];
    if (options.length <= 1) return options[0];
    const seen = this.visibleCounts(id);
    const tried = options.map((action) => {
      const h = hand.clone().dapai(action);
      return { action, h, x: Majiang.Util.xiangting(h) };
    });
    const best = Math.min(...tried.map((t) => t.x));
    let pick = null;
    let top = -Infinity;
    for (const { action, h, x } of tried) {
      if (x !== best) continue;
      const outs = (Majiang.Util.tingpai(h) || []).reduce((n, t) => n + Math.max(0, 4 - seen[t]), 0);
      let danger = 0;
      for (const p of this.players) {
        if (p.id !== id && p.hand.lizhi && !p.river.some((r) => r.tile === action.slice(0, 2))) danger += x > 0 ? 5 : 1;
      }
      const value = outs - danger + (action[0] === 'z' ? 0.05 : Math.abs(+action[1] - 5) * 0.008);
      if (value > top) {
        top = value;
        pick = action;
      }
    }
    return pick || options[0];
  }

  cpuCall(id, tile, melds) {
    const hand = this.players[id].hand;
    const shanten = Majiang.Util.xiangting(hand);
    const simple = (t) => t[0] !== 'z' && +t[1] > 1 && +t[1] < 9;
    const allSimples = handTiles(hand).every(simple) && simple(tile) && hand._fulou.every((m) => meldTiles(m).every(simple));
    const yakuhai = (t) => t[0] === 'z' && (+t[1] >= 5 || +t[1] === 1 || +t[1] === this.seat(id) + 1);
    const hasYakuhai = hand._fulou.some((m) => new Set(meldTiles(m)).size === 1 && yakuhai(meldTiles(m)[0]));
    for (const meld of melds) {
      const isYakuhaiPon = new Set(meldTiles(meld)).size === 1 && yakuhai(tile);
      if (!allSimples && !hasYakuhai && !isYakuhaiPon) continue;
      const after = hand.clone().fulou(meld);
      const out = this.chooseDiscard(id, after);
      if (!out) continue;
      if (Majiang.Util.xiangting(after.clone().dapai(out)) < shanten) return meld;
    }
    return null;
  }

  actionClock(id = 0) {
    if (!['playing', 'paused'].includes(this.status)) return null;
    const p = this.players[id];
    if (!p.hand._zimo && !this.wall.length) return null;
    return {
      kind: p.hand._zimo ? 'discard' : 'draw',
      remainingMs: Math.max(0, (p.hand.lizhi ? p.next : p.deadline) - this.elapsed),
      limitMs: p.hand.lizhi ? RIICHI_STEP : ACTION_LIMIT,
      automatic: p.hand.lizhi,
      running: this.status === 'playing',
    };
  }

  forceAction(id) {
    const p = this.players[id];
    if (this.status !== 'playing') return false;
    if (!p.hand._zimo) {
      if (!this.wall.length) return false;
      const tile = this.wall.at(-1);
      this.lastTimeout = { player: id, action: 'draw', tile };
      p.lastTimeout = this.lastTimeout;
      p.timeoutCount++;
      if (!this.draw(id, { automatic: true })) throw new Error('Automatic draw failed.');
      if (this.status === 'playing') this.notify('timeout', `${p.name}: 5 seconds passed, tile drawn automatically.`);
      return true;
    }
    const tsumogiri = p.hand._zimo.length === 2;
    const tile = tsumogiri ? p.hand._zimo + '_' : p.hand.get_dapai()?.at(-1);
    if (!tile) throw new Error('No tile to discard on timeout.');
    if (id === 0) this.riichiArmed = false;
    this.lastTimeout = { player: id, action: tsumogiri ? 'tsumogiri' : 'discard', tile: tile.slice(0, 2) };
    p.lastTimeout = this.lastTimeout;
    p.timeoutCount++;
    if (!this.discard(id, tile, false, { automatic: true })) throw new Error('Automatic discard failed.');
    if (this.status === 'playing') {
      this.notify('timeout', `${p.name}: 5 seconds passed, ${tileName(tile.slice(0, 2))} ${tsumogiri ? 'discarded straight from the draw' : 'discarded automatically'}.`);
    }
    return true;
  }

  tick(dt, { holdAutoDraw = false } = {}) {
    if (this.status !== 'playing' || !Number.isFinite(dt) || dt < 0) return;
    const until = this.elapsed + dt;
    while (this.status === 'playing') {
      const next = this.players
        .map((p) => {
          if (!p.hand._zimo && !this.wall.length) return null;
          const auto =
            p.hand.lizhi ||
            !this.isHuman(p.id) ||
            (p.autoDraw && !(p.id === 0 && holdAutoDraw) && this.elapsed >= p.holdUntil && !p.hand._zimo);
          const at = auto ? p.next : Infinity;
          return { p, at: Math.max(this.elapsed, Math.min(p.deadline, at)), forced: p.deadline <= at };
        })
        .filter(Boolean)
        .sort((a, b) => a.at - b.at || a.p.id - b.p.id)[0];
      if (!next || next.at > until) break;
      this.elapsed = next.at;
      if (next.forced) this.forceAction(next.p.id);
      else if (next.p.hand._zimo) {
        const tile = next.p.hand.lizhi ? next.p.hand._zimo + '_' : this.chooseDiscard(next.p.id);
        if (!tile || !this.discard(next.p.id, tile, this.riichiDiscards(next.p.id).includes(tile), { automatic: true })) {
          throw new Error('CPU could not discard.');
        }
      } else if (!this.draw(next.p.id, { automatic: true })) throw new Error('Automatic draw failed.');
      this.checkExhausted();
    }
    if (this.status === 'playing') this.elapsed = until;
    this.checkExhausted();
  }

  checkExhausted() {
    if (this.status !== 'playing' || this.wall.length || this.players.some((p) => p.hand._zimo)) return;
    const tenpai = this.players.map((p) => Majiang.Util.xiangting(p.hand) === 0);
    const n = tenpai.filter(Boolean).length;
    const delta = tenpai.map((t) => (n === 0 || n === 4 ? 0 : t ? 3000 / n : -3000 / (4 - n)));
    this.scores = this.scores.map((s, i) => s + delta[i]);
    this.status = 'ended';
    this.result = { type: 'draw', tenpai, delta };
    this.finishPot();
    this.notify('end', 'The wall ran out. Exhaustive draw.');
  }

  win(id, from, tile, score) {
    const delta = [0, 0, 0, 0];
    score.fenpei.forEach((d, seat) => {
      delta[(seat + this.dealer) % 4] = d;
    });
    this.scores = this.scores.map((s, i) => s + delta[i]);
    this.pot = 0;
    this.status = 'ended';
    this.riichiArmed = false;
    this.result = { type: from === null ? 'tsumo' : 'ron', winner: id, from, tile, score, delta };
    this.notify('win', `${this.players[id].name} wins by ${from === null ? 'tsumo' : 'ron'}!`);
  }

  finishPot() {
    if (this.round === 3 && this.pot) {
      const top = this.ranking()[0];
      const amount = this.pot * 1000;
      this.scores[top] += amount;
      this.result.delta[top] += amount;
      this.result.potAward = { player: top, amount };
      this.pot = 0;
    }
  }

  ranking() {
    return [0, 1, 2, 3].sort((a, b) => this.scores[b] - this.scores[a] || a - b);
  }

  snapshot() {
    const me = this.players[0];
    return {
      status: this.status,
      round: roundName(this.round),
      wall: this.wall.length,
      scores: this.scores.slice(),
      hand: handTiles(me.hand),
      melds: me.hand._fulou.slice(),
      canDraw: this.status === 'playing' && !me.hand.lizhi && !me.hand._zimo && this.wall.length > 0,
      legalDiscards: me.hand.lizhi ? [] : me.hand.get_dapai() || [],
      reservations: this.reservations.map((r) => ({ ...r })),
      result: this.result,
      actionClock: this.actionClock(),
      lastTimeout: this.lastTimeout,
      players: this.players.map((p) => ({ id: p.id, riichi: p.hand.lizhi, river: p.river.map((r) => ({ ...r })) })),
    };
  }

  // What one seat may see in an online match: their own hand, everyone's public tiles.
  viewFor(seat) {
    const ended = this.status === 'ended';
    const r = this.result;
    const me = this.players[seat];
    return {
      status: this.status,
      round: this.round,
      dealer: this.dealer,
      scores: this.scores.slice(),
      pot: this.pot,
      wallCount: this.wall.length,
      elapsed: this.elapsed,
      revision: this.revision,
      cpuSpeed: this.speed,
      dora: this.dead[0],
      ura: ended && r?.winner != null && this.players[r.winner].hand.lizhi ? this.dead[1] : null,
      result: r,
      ranking: this.ranking(),
      lastDiscard: this.lastDiscard,
      // A timed-out draw records the drawn tile; only its owner may see it.
      lastTimeout: this.lastTimeout && { ...this.lastTimeout, tile: this.lastTimeout.action === 'draw' && this.lastTimeout.player !== seat ? null : this.lastTimeout.tile },
      lastEvent: this.lastEvent,
      log: this.log.slice(0, 20),
      reservations: me.reservations.map((x) => ({ ...x })),
      autoDraw: me.autoDraw,
      riichiDiscards: this.status === 'playing' ? this.riichiDiscards(seat) : [],
      waits: this.waits(seat),
      furiten: this.furiten(seat),
      clock: this.actionClock(seat),
      players: this.players.map((p) => {
        const out = {
          id: p.id,
          name: p.name,
          river: p.river.map((x) => ({ ...x })),
          draws: p.draws,
          riichi: p.hand.lizhi,
          melds: p.hand._fulou.slice(),
          holding: !!p.hand._zimo,
          deadline: p.deadline,
          human: this.isHuman(p.id),
        };
        if (p.id === seat) Object.assign(out, { phase: p.phase, timeoutCount: p.timeoutCount, lastTimeout: p.lastTimeout });
        if (p.id === seat || (ended && r?.winner === p.id)) out.hand = p.hand.toString();
        return out;
      }),
    };
  }
}
